import { MarqueeRow } from '@marketing/components'
import type { ReviewCard } from '@/pages/home/main.content'

interface ReviewsSectionProps {
  title: string
  titleHighlight: string
  ctaLabel: string
  cards: ReviewCard[]
  onMoreClick?: () => void
}

/** Photo review card — 320×416, full bg image + bottom overlay */
function PhotoCard({ card }: { card: Extract<ReviewCard, { type: 'photo' }> }) {
  return (
    <article className="relative w-[280px] lg:w-[320px] h-[380px] lg:h-[416px] rounded-[24px] overflow-hidden shrink-0">
      <img
        src={card.photo}
        alt={card.title}
        className="absolute inset-0 w-full h-full object-cover"
      />
      {card.author ? (
        <div className="absolute bottom-0 left-0 right-0 h-[167px] bg-qb-overlay-70 flex flex-col justify-end gap-4 px-8 pb-8 pt-6 text-white tracking-[-0.6px]">
          <p className="font-['Pretendard',sans-serif] font-medium text-[20px] lg:text-[24px] leading-[1.4] whitespace-pre-line">
            {card.title}
          </p>
          <p className="font-['Pretendard',sans-serif] font-semibold text-[16px] lg:text-[18px] leading-[1.5]">
            {card.author}
          </p>
        </div>
      ) : null}
    </article>
  )
}

/** Text review card — 320×416, solid bg color + title + content + avatar bottom-right */
function TextCard({ card }: { card: Extract<ReviewCard, { type: 'text' }> }) {
  return (
    <article
      className="relative w-[280px] lg:w-[320px] h-[380px] lg:h-[416px] rounded-[24px] overflow-hidden shrink-0 flex flex-col justify-between pt-8 pb-6 px-6"
      style={{ backgroundColor: card.backgroundColor }}
    >
      {/* Text block */}
      <div className="flex flex-col gap-4 text-white tracking-[-0.6px]">
        <p className="font-['Pretendard',sans-serif] font-bold text-[20px] lg:text-[24px] leading-[1.4] whitespace-pre-line">
          {card.title}
        </p>
        <p className="font-['Pretendard',sans-serif] font-medium text-[15px] lg:text-[18px] leading-[1.5] whitespace-pre-line">
          {card.content}
        </p>
        <p className="font-['Pretendard',sans-serif] font-semibold text-[15px] lg:text-[18px] leading-[1.5]">
          {card.author}
        </p>
      </div>

      {/* Avatar bottom-right */}
      <div className="absolute bottom-[18px] right-[18px] lg:bottom-6 lg:right-6 w-[112px] h-[112px] lg:w-[136px] lg:h-[136px] rounded-full overflow-hidden">
        <img
          src={card.avatar}
          alt={card.author}
          className="w-full h-full object-cover"
        />
      </div>
    </article>
  )
}

/**
 * #07 Reviews — header (title left + CTA right) + MarqueeRow of 10 cards.
 * Figma: node 37:12592 (desktop) / 194:5661 (mobile)
 */
export function ReviewsSection({
  title,
  titleHighlight,
  ctaLabel,
  cards,
  onMoreClick,
}: ReviewsSectionProps) {
  return (
    <section id="reviews" data-marketing-section="" className="w-full bg-qb-bg-tertiary overflow-hidden">
      {/* Header */}
      <div className="mx-auto max-w-[1136px] px-6 lg:px-0 pt-14 lg:pt-[120px] pb-10 lg:pb-[80px] flex flex-col items-center gap-6 lg:gap-10">
        <p className="font-['Pretendard',sans-serif] font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2] tracking-[-0.6px] text-center">
          <span className="text-qb-text-high">{title.trim()}</span>
          <br className="lg:hidden" aria-hidden="true" />
          <span className="lg:ml-2 text-qb-primary">{titleHighlight}</span>
        </p>
        {onMoreClick && <button
          onClick={onMoreClick}
          className="inline-flex items-center justify-center h-[56px] min-w-[120px] max-w-[432px] px-6 py-2 rounded-full bg-qb-primary hover:bg-qb-primary-hover transition-colors font-['Pretendard',sans-serif] font-semibold text-[20px] leading-[1.5] tracking-[-0.6px] text-white shadow-[0px_4px_4px_rgba(0,0,0,0.1)]"
        >
          {ctaLabel}
        </button>}
      </div>

      {/* Marquee */}
      <div className="pb-14 lg:pb-[100px]">
        <MarqueeRow speed="slow" pauseOnHover gap={16}>
          {cards.map((card) =>
            card.type === 'photo' ? (
              <PhotoCard key={card.id} card={card} />
            ) : (
              <TextCard key={card.id} card={card} />
            ),
          )}
        </MarqueeRow>
      </div>
    </section>
  )
}
